import React, { useEffect, useState } from 'react';
import { supabase } from '../../../services/supabaseClient';

const UniversitiesCatalogPage = () => {
  const [activeTab, setActiveTab] = useState('universities');
  const [universities, setUniversities] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null); // { type: 'success'|'error'|'info', text: string }
  const [universityFilter, setUniversityFilter] = useState('');
  const [programSearchFilter, setProgramSearchFilter] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [{ data: unis }, { data: progs }, { data: ints }] = await Promise.all([
        supabase.from('universities').select('*').order('name'),
        supabase.from('programs').select('*, universities!inner(name)').order('name'),
        supabase.from('intakes').select('*, programs!inner(name)').order('apply_close', { ascending: true })
      ]);
      setUniversities(unis || []);
      setPrograms(progs || []);
      setIntakes(ints || []);
    } catch (e) {
      setError('Failed to load catalog');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const showToast = (type, text) => {
    setToast({ type, text });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), 2500);
  };

  const addUniversity = async (u) => {
    if (!u.name?.trim()) {
      showToast('error', 'University name is required');
      return;
    }

    let result;
    if (u.id) {
      // Update
      result = await supabase.from('universities').update({
        name: u.name,
        country: u.country,
        website: u.website
      }).eq('id', u.id);
    } else {
      // Insert
      result = await supabase.from('universities').insert([u]);
    }

    const { error } = result;
    if (error) {
      console.error('university save error', error);
      showToast('error', error.message || 'Failed to save university');
    }
    else {
      showToast('success', u.id ? 'University updated' : 'University added');
      setUForm({ name: '', country: '', website: '' }); // Reset form
      await load();
    }
  };

  const addProgram = async (p) => {
    if (!p.university_id) return showToast('error', 'Select a university');
    if (!p.name.trim()) return showToast('error', 'Program name is required');

    const payload = { ...p };

    // Remove joined fields that are not columns
    delete payload.universities;

    // Sanitize numeric fields
    ['min_ielts', 'min_gpa', 'min_al_passes', 'tuition'].forEach(field => {
      if (payload[field] === '') payload[field] = null;
    });
    // Sanitize dates
    ['application_opening', 'application_deadline', 'aptitude_test_deadline'].forEach(field => {
      if (payload[field] === '') payload[field] = null;
    });

    let result;
    if (payload.id) {
      // Update
      result = await supabase.from('programs').update(payload).eq('id', payload.id);
    } else {
      // Insert
      result = await supabase.from('programs').insert([payload]);
    }

    const { error } = result;
    if (error) {
      console.error('program save error', error);
      showToast('error', error.message || 'Failed to save program');
    }
    else {
      showToast('success', payload.id ? 'Program updated' : 'Program added');
      setPForm({
        university_id: '', name: '', level: '', discipline: '', course_of_study: '',
        semesters: '', application_method: '', application_opening: '', application_deadline: '',
        min_al_passes: '', min_ielts: '', min_gpa: '', tuition: '', tuition_fee_type: '', currency: 'EUR',
        has_aptitude_test: false, aptitude_test_submission: '', aptitude_test_deadline: ''
      });
      await load();
    }
  };

  const addIntake = async (i) => {
    if (!i.program_id) return showToast('error', 'Select a program');
    if (!i.term?.trim()) return showToast('error', 'Term is required');

    const payload = { ...i };
    // Sanitize dates
    ['start_date', 'apply_open', 'apply_close'].forEach(field => {
      if (payload[field] === '') payload[field] = null;
    });

    const { error } = await supabase.from('intakes').insert([payload]);
    if (error) {
      console.error('intake insert error', error);
      showToast('error', error.message || 'Failed to add intake');
    }
    else {
      showToast('success', 'Intake added');
      // Reset form
      setIForm({ program_id: '', term: '', season: '', year: '', start_date: '', apply_open: '', apply_close: '' });
      setIForm({ program_id: '', term: '', season: '', year: '', start_date: '', apply_open: '', apply_apply_close: '' });
      await load();
    }
  };

  // Local form states
  const [uForm, setUForm] = useState({ name: '', country: '', website: '' });
  const [pForm, setPForm] = useState({
    university_id: '', name: '', level: '', discipline: '', course_of_study: '',
    semesters: '', application_method: '', application_opening: '', application_deadline: '',
    min_al_passes: '', min_ielts: '', min_gpa: '', tuition: '', tuition_fee_type: '', currency: 'EUR',
    has_aptitude_test: false, aptitude_test_submission: '', aptitude_test_deadline: ''
  });
  const [iForm, setIForm] = useState({ program_id: '', term: '', season: '', year: '', start_date: '', apply_open: '', apply_close: '' });

  if (loading) return (
    <div className="min-h-screen bg-gray-900 font-mono text-white flex flex-col items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
      <div>Loading university catalog...</div>
    </div>
  );

  const q = search.trim().toLowerCase();
  const filteredUnis = universities.filter(u => `${u.name} ${u.country}`.toLowerCase().includes(q));
  const filteredProgs = programs.filter(p => `${p.name} ${p.level || ''} ${p.discipline || ''}`.toLowerCase().includes(q));
  const filteredIntakes = intakes.filter(i => `${i.term || ''} ${i.apply_close || ''} ${i.programs?.name || ''}`.toLowerCase().includes(q));

  // Filter programs for intake form based on university and search filters
  const programsForIntakeForm = programs.filter(p => {
    const matchesUniversity = !universityFilter || p.university_id === universityFilter;
    const matchesSearch = !programSearchFilter || p.name.toLowerCase().includes(programSearchFilter.toLowerCase());
    return matchesUniversity && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">🎓 University Catalog</h1>
        <p className="text-gray-400">Manage universities, programs, and intakes</p>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`admin-alert ${toast.type === 'success' ? 'admin-alert-success' : toast.type === 'error' ? 'admin-alert-error' : 'admin-alert-info'}`}>
          {toast.text}
        </div>
      )}

      {/* Tabs */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg shadow">
        <div className="p-6">
          <div className="admin-tab-header">
            <div className="admin-tab-nav">
              <button
                onClick={() => setActiveTab('universities')}
                className={`admin-tab-btn ${activeTab === 'universities' ? 'admin-tab-btn-active' : ''}`}
              >
                🏛️ Universities
              </button>
              <button
                onClick={() => setActiveTab('programs')}
                className={`admin-tab-btn ${activeTab === 'programs' ? 'admin-tab-btn-active' : ''}`}
              >
                📚 Programs
              </button>
              <button
                onClick={() => setActiveTab('intakes')}
                className={`admin-tab-btn ${activeTab === 'intakes' ? 'admin-tab-btn-active' : ''}`}
              >
                📅 Intakes
              </button>
            </div>
            <input
              className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="🔍 Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 rounded border mb-6 bg-red-900/20 border-red-800 text-red-400">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      {activeTab === 'universities' && (
        <div className="admin-section">
          {/* Add University Form */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg shadow">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">{uForm.id ? 'Edit University' : 'Add University'}</h3>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <input
                    value={uForm.name}
                    onChange={(e) => setUForm({ ...uForm, name: e.target.value })}
                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="University name"
                  />
                  <input
                    value={uForm.country}
                    onChange={(e) => setUForm({ ...uForm, country: e.target.value })}
                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Country"
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    <input
                      value={uForm.website}
                      onChange={(e) => setUForm({ ...uForm, website: e.target.value })}
                      className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Website"
                    />
                    <button
                      onClick={() => {
                        if (!uForm.name.trim()) return;
                        addUniversity(uForm);
                      }}
                      className="px-3 py-1.5 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
                    >
                      {uForm.id ? 'Update' : 'Add'}
                    </button>
                    {uForm.id && (
                      <button
                        onClick={() => setUForm({ name: '', country: '', website: '' })}
                        className="px-3 py-1.5 rounded text-sm font-medium bg-gray-600 text-white hover:bg-gray-500"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Universities Table */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg shadow">
            <div className="p-6">
              <div className="admin-table-container">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Country</th>
                      <th>Website</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUnis.map(u => (
                      <tr key={u.id}>
                        <td className="admin-table-strong">{u.name}</td>
                        <td>{u.country || '-'}</td>
                        <td>
                          {u.website ? (
                            <a href={u.website} target="_blank" rel="noreferrer" className="admin-link">
                              {u.website}
                            </a>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="text-right space-x-2">
                          <button
                            onClick={() => window.location.href = `/admin/universities/${u.id}`}
                            className="text-blue-400 hover:text-blue-300 text-sm"
                          >
                            View
                          </button>
                          <button
                            onClick={() => {
                              setUForm(u);
                              // Scroll to top to see form
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            className="text-yellow-400 hover:text-yellow-300 text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={async () => {
                              if (!window.confirm('Are you sure you want to delete this university?')) return;
                              const { error } = await supabase.from('universities').delete().eq('id', u.id);
                              if (error) showToast('error', 'Failed to delete university');
                              else {
                                showToast('success', 'University deleted');
                                load();
                              }
                            }}
                            className="text-red-400 hover:text-red-300 text-sm"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filteredUnis.length === 0 && (
                      <tr>
                        <td colSpan={4} className="text-center py-8 text-gray-500">No universities found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'programs' && (
        <div className="admin-section">
          {/* Add Program Form */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg shadow">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">{pForm.id ? 'Edit Program' : 'Add Program'}</h3>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <select
                    value={pForm.university_id || ''}
                    onChange={(e) => setPForm({ ...pForm, university_id: e.target.value })}
                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select university</option>
                    {universities.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                  <input
                    value={pForm.name || ''}
                    onChange={(e) => setPForm({ ...pForm, name: e.target.value })}
                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Program name"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={pForm.level || ''}
                      onChange={(e) => setPForm({ ...pForm, level: e.target.value })}
                      className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Level</option>
                      <option value="Bachelors">Bachelors</option>
                      <option value="Masters">Masters</option>
                      <option value="PhD">PhD</option>
                    </select>
                    <select
                      value={pForm.course_of_study || ''}
                      onChange={(e) => setPForm({ ...pForm, course_of_study: e.target.value })}
                      className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Course of Study</option>
                      <option value="Agricultural and Forest Sciences">Agricultural and Forest Sciences</option>
                      <option value="Humanities and Social Sciences">Humanities and Social Sciences</option>
                      <option value="Engineering Sciences">Engineering Sciences</option>
                      <option value="Art, Music, Design">Art, Music, Design</option>
                      <option value="Mathematics, Natural Sciences">Mathematics, Natural Sciences</option>
                      <option value="Medicine, Health Sciences">Medicine, Health Sciences</option>
                      <option value="Language and Cultural Studies">Language and Cultural Studies</option>
                      <option value="Economic Sciences, Law">Economic Sciences, Law</option>
                      <option value="Teaching Degrees">Teaching Degrees</option>
                      <option value="Public Administration">Public Administration</option>
                    </select>
                  </div>
                  <input
                    value={pForm.discipline || ''}
                    onChange={(e) => setPForm({ ...pForm, discipline: e.target.value })}
                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Discipline (e.g. Computer Science)"
                  />
                  <input
                    value={pForm.semesters || ''}
                    onChange={(e) => setPForm({ ...pForm, semesters: e.target.value })}
                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Semesters (e.g. 8)"
                  />
                  <select
                    value={pForm.application_method || ''}
                    onChange={(e) => setPForm({ ...pForm, application_method: e.target.value })}
                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Application Method</option>
                    <option value="Uni Assist">Uni Assist</option>
                    <option value="Direct with VPD">Direct with VPD</option>
                    <option value="Direct">Direct</option>
                  </select>

                  {/* Tuition Fee */}
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      type="number"
                      value={pForm.tuition || ''}
                      onChange={(e) => setPForm({ ...pForm, tuition: e.target.value })}
                      className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Tuition Fee"
                    />
                    <select
                      value={pForm.currency || ''}
                      onChange={(e) => setPForm({ ...pForm, currency: e.target.value })}
                      className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Currency</option>
                      <option value="EUR">Euro (€)</option>
                      <option value="USD">Dollar ($)</option>
                      <option value="LKR">LKR (Rs)</option>
                      <option value="CAD">CAD ($)</option>
                      <option value="AUD">AUD ($)</option>
                    </select>
                    <select
                      value={pForm.tuition_fee_type || ''}
                      onChange={(e) => setPForm({ ...pForm, tuition_fee_type: e.target.value })}
                      className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Fee Type</option>
                      <option value="Per Semester">Per Semester</option>
                      <option value="Per Year">Per Year</option>
                      <option value="Total">Total</option>
                    </select>
                  </div>

                  {/* Dates */}
                  <div className="flex flex-col">
                    <label className="text-xs text-gray-400 mb-1">App Opening</label>
                    <input
                      type="date"
                      value={pForm.application_opening || ''}
                      onChange={(e) => setPForm({ ...pForm, application_opening: e.target.value })}
                      className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs text-gray-400 mb-1">App Deadline</label>
                    <input
                      type="date"
                      value={pForm.application_deadline || ''}
                      onChange={(e) => setPForm({ ...pForm, application_deadline: e.target.value })}
                      className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Requirements */}
                  <input
                    type="number"
                    value={pForm.min_al_passes || ''}
                    onChange={(e) => setPForm({ ...pForm, min_al_passes: e.target.value })}
                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Min A/L Passes (1-3)"
                  />
                  <input
                    type="number"
                    step="0.1"
                    value={pForm.min_ielts || ''}
                    onChange={(e) => setPForm({ ...pForm, min_ielts: e.target.value })}
                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Min IELTS Band"
                  />
                  <input
                    type="number"
                    step="0.01"
                    value={pForm.min_gpa || ''}
                    onChange={(e) => setPForm({ ...pForm, min_gpa: e.target.value })}
                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Min GPA"
                  />

                  {/* Aptitude Test */}
                  <div className="flex items-center gap-2 border border-gray-600 rounded px-3 py-2 bg-gray-700">
                    <input
                      type="checkbox"
                      checked={pForm.has_aptitude_test || false}
                      onChange={(e) => setPForm({ ...pForm, has_aptitude_test: e.target.checked })}
                      className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-500 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-300">Aptitude Test?</span>
                  </div>

                  {pForm.has_aptitude_test && (
                    <>
                      <input
                        value={pForm.aptitude_test_submission || ''}
                        onChange={(e) => setPForm({ ...pForm, aptitude_test_submission: e.target.value })}
                        className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Submission Method"
                      />
                      <div className="flex flex-col">
                        <label className="text-xs text-gray-400 mb-1">Test Deadline</label>
                        <input
                          type="date"
                          value={pForm.aptitude_test_deadline || ''}
                          onChange={(e) => setPForm({ ...pForm, aptitude_test_deadline: e.target.value })}
                          className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </>
                  )}

                  <div className="col-span-full flex justify-end mt-4 gap-2">
                    <button
                      onClick={() => {
                        if (!pForm.name.trim() || !pForm.university_id) return;
                        addProgram(pForm);
                      }}
                      className="px-6 py-2 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
                    >
                      {pForm.id ? 'Update Program' : 'Add Program'}
                    </button>
                    {pForm.id && (
                      <button
                        onClick={() => setPForm({
                          university_id: '', name: '', level: '', discipline: '',
                          semesters: '', application_method: '', application_opening: '', application_deadline: '',
                          min_al_passes: '', min_ielts: '', min_gpa: '',
                          has_aptitude_test: false, aptitude_test_submission: '', aptitude_test_deadline: ''
                        })}
                        className="px-6 py-2 rounded text-sm font-medium bg-gray-600 text-white hover:bg-gray-500"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Programs Table */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg shadow">
            <div className="p-6">
              <div className="admin-table-container">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr>
                      <th>Program</th>
                      <th>University</th>
                      <th>Level</th>
                      <th>Discipline</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProgs.map(p => (
                      <tr key={p.id}>
                        <td className="admin-table-strong">{p.name}</td>
                        <td>{p.universities?.name || '-'}</td>
                        <td>{p.level || '-'}</td>
                        <td>{p.discipline || '-'}</td>
                        <td className="text-right space-x-2">
                          <button
                            onClick={() => window.location.href = `/admin/programs/${p.id}`}
                            className="text-blue-400 hover:text-blue-300 text-sm"
                          >
                            View
                          </button>
                          <button
                            onClick={() => {
                              setPForm(p);
                              // Scroll to top to see form
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            className="text-yellow-400 hover:text-yellow-300 text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={async () => {
                              if (!window.confirm('Are you sure you want to delete this program?')) return;
                              const { error } = await supabase.from('programs').delete().eq('id', p.id);
                              if (error) showToast('error', 'Failed to delete program');
                              else {
                                showToast('success', 'Program deleted');
                                load();
                              }
                            }}
                            className="text-red-400 hover:text-red-300 text-sm"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filteredProgs.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center py-8 text-gray-500">No programs found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'intakes' && (
        <div className="admin-section">
          {/* Filters */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg shadow mb-6">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">🔍 Filters</h3>
                <button
                  onClick={() => {
                    setUniversityFilter('');
                    setProgramSearchFilter('');
                  }}
                  className="text-sm text-gray-400 hover:text-white"
                >
                  Clear Filters
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Filter by University</label>
                  <select
                    value={universityFilter}
                    onChange={(e) => setUniversityFilter(e.target.value)}
                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All Universities</option>
                    {universities.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Search Programs</label>
                  <input
                    value={programSearchFilter}
                    onChange={(e) => setProgramSearchFilter(e.target.value)}
                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Type program name..."
                  />
                </div>
              </div>
              <div className="mt-3 text-sm text-gray-400">
                Showing {programsForIntakeForm.length} of {programs.length} programs
              </div>
            </div>
          </div>

          {/* Add Intake Form */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg shadow">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">Add Intake</h3>
              </div>
              <div className="space-y-4">
                <div className="admin-grid admin-grid-5">
                  <select
                    value={iForm.program_id}
                    onChange={(e) => setIForm({ ...iForm, program_id: e.target.value })}
                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select program</option>
                    {programsForIntakeForm.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name} - {p.universities?.name || 'Unknown University'}
                      </option>
                    ))}
                  </select>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={iForm.season || ''}
                      onChange={(e) => {
                        const season = e.target.value;
                        setIForm(prev => ({ ...prev, season, term: `${season} ${prev.year || ''}`.trim() }));
                      }}
                      className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Season</option>
                      <option value="Summer">Summer</option>
                      <option value="Winter">Winter</option>
                      <option value="Spring">Spring</option>
                      <option value="Fall">Fall</option>
                    </select>
                    <input
                      value={iForm.year || ''}
                      onChange={(e) => {
                        const year = e.target.value;
                        setIForm(prev => ({ ...prev, year, term: `${prev.season || ''} ${year}`.trim() }));
                      }}
                      className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Year (2025)"
                    />
                  </div>
                  <input
                    value={iForm.start_date || ''}
                    onChange={(e) => setIForm({ ...iForm, start_date: e.target.value })}
                    type="date"
                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex flex-col">
                      <label className="text-xs text-gray-400 mb-1">Apply Open</label>
                      <input
                        value={iForm.apply_open || ''}
                        onChange={(e) => setIForm({ ...iForm, apply_open: e.target.value })}
                        type="date"
                        className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-xs text-gray-400 mb-1">Apply Close</label>
                      <input
                        value={iForm.apply_close || ''}
                        onChange={(e) => setIForm({ ...iForm, apply_close: e.target.value })}
                        type="date"
                        className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end mt-4">
                    <button
                      onClick={() => {
                        if (!iForm.program_id || !iForm.term.trim()) return;
                        addIntake(iForm);
                        setIForm({ program_id: '', term: '', season: '', year: '', start_date: '', apply_open: '', apply_close: '' });
                      }}
                      className="px-6 py-2 rounded text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Add Intake
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Intakes Table */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg shadow">
            <div className="p-6">
              <div className="admin-table-container">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr>
                      <th>Program</th>
                      <th>Term</th>
                      <th>Apply Close</th>
                      <th>Start</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredIntakes.map(i => (
                      <tr key={i.id}>
                        <td className="admin-table-strong">{i.programs?.name || '-'}</td>
                        <td>{i.term || '-'}</td>
                        <td>{i.apply_close || '-'}</td>
                        <td>{i.start_date || '-'}</td>
                      </tr>
                    ))}
                    {filteredIntakes.length === 0 && (
                      <tr>
                        <td colSpan={4} className="text-center py-8 text-gray-500">No intakes found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div >
      )}
    </div >
  );
};

export default UniversitiesCatalogPage;
